import React, { useMemo } from 'react';
import { format0 } from 'utils/number';
import { endSentence } from 'utils/strings';
import {
  Tile,
  RadioButtonGroup,
  RadioButton,
  Select,
  SelectItem,
} from '@carbon/react';
import { OperatorTag } from 'components/operator-tag';
import {
  ENTITY_TYPE_LABEL,
  type ReconcileCandidate,
} from 'pages/documents/structuring/reconcile/types';
import ReconcileSourceDocuments from 'pages/documents/structuring/reconcile/reconcile-source-documents';
import {
  survivors,
  representativeId,
  type ConsolidationState,
  type ConsolidationAction,
} from './consolidation-utils';
import styles from './entity-consolidation-survivors.module.scss';

interface Props {
  state: ConsolidationState;
  dispatch: (action: ConsolidationAction) => void;
  // Display data (name, type, addresses) keyed by entity id — the presentation
  // side of the model (the reducer only tracks membership/selection).
  candidatesById: Map<number, ReconcileCandidate>;
  // Click-to-highlight, owned by the tearsheet (the source entity of the clicked
  // document row so its left-pane candidate row lights up too, and so the panel's
  // distinct-name selection and this can't both be highlighted at once).
  highlightedEntityId: number | null;
  onHighlightEntity: (entityId: number | null) => void;
  // Opens a document in the shared inline Document viewer pane instead of a
  // new tab.
  onViewDocument?: (documentId: number, documentName?: string) => void;
  viewedDocumentId?: number | null;
  showOperatorTag?: boolean;
}

// Reconciliation Result Preview: a section BELOW the Entity Consolidation table
// with one block per surviving entity. Each block lets the examiner confirm
// which record survives (the "Surviving entity" radio → the representative),
// pick the survivor's primary address, and expand "View Documents" to see the
// source records — the provenance behind the merge.
const EntityConsolidationSurvivors: React.FC<Props> = ({
  state,
  dispatch,
  candidatesById,
  highlightedEntityId,
  onHighlightEntity,
  onViewDocument,
  viewedDocumentId,
  showOperatorTag = false,
}) => {
  const survivorNames = useMemo(() => survivors(state), [state]);

  if (survivorNames.length === 0) return null;

  return (
    <section className={styles.root} aria-label="Reconciliation result preview">
      <h4 className={styles.heading}>Reconciliation result</h4>
      {survivorNames.map((name, index) => {
        const members = name.checkedIds
          .map((id) => candidatesById.get(id))
          .filter((c): c is ReconcileCandidate => Boolean(c));
        const rep = representativeId(state, name);
        // An Imported record can't survive when a canonical (Individual /
        // Organization) record shares the name — the canonical always wins the
        // merge — so its radio is disabled.
        const hasCanonical = members.some((m) => m.type !== 'ImportedEntity');

        // The primary address draws from EVERY entity folded into the final
        // consolidated result — the survivor's own members PLUS every name
        // deduped into it — not just its own literal-name group.
        const foldedMembers = [
          ...name.checkedIds,
          ...state.names
            .filter((n) => n.dedupedInto === name.key)
            .flatMap((n) => n.checkedIds),
        ]
          .map((id) => candidatesById.get(id))
          .filter((c): c is ReconcileCandidate => Boolean(c));

        // Primary-address options — the union of every folded member's
        // selectable addresses, deduped by id.
        const addressOptions: Array<{ id: number; label: string }> = [];
        const seen = new Set<number>();
        foldedMembers.forEach((m) =>
          (m.addresses ?? []).forEach((a) => {
            if (!seen.has(a.id)) {
              seen.add(a.id);
              addressOptions.push(a);
            }
          })
        );

        // Reflect the FULL merged set (own members + every name deduped into this
        // survivor), not just the survivor's own literal-name members — otherwise a
        // survivor absorbing several other names reads "kept as its own distinct
        // record" on the final confirmation surface.
        const subtitle =
          foldedMembers.length > 1
            ? `${format0(
                foldedMembers.length
              )} records merge — choose which one survives as ${endSentence(
                name.label
              )}`
            : `Kept as its own distinct record — choose its primary address.`;

        return (
          <Tile key={name.key} className={styles.block}>
            <h5 className={styles.blockTitle}>{`Survivor ${index + 1}`}</h5>
            <p className={styles.subtitle}>{subtitle}</p>

            <RadioButtonGroup
              name={`survivor-rep-${name.key}`}
              orientation="vertical"
              legendText="Surviving entity"
              className={styles.radios}
              valueSelected={rep !== null ? String(rep) : ''}
              onChange={(value: string | number | undefined) =>
                value !== undefined &&
                dispatch({
                  type: 'setRepresentative',
                  key: name.key,
                  id: Number(value),
                })
              }
            >
              {members.map((m) => (
                <RadioButton
                  key={m.id}
                  id={`survivor-rep-${name.key}-${m.id}`}
                  value={String(m.id)}
                  disabled={hasCanonical && m.type === 'ImportedEntity'}
                  labelText={
                    <>
                      {`${m.name} — ${ENTITY_TYPE_LABEL[m.type]} · #${m.id}`}
                      {showOperatorTag && m.operator && (
                        <span className={styles.labelTag}>
                          <OperatorTag />
                        </span>
                      )}
                    </>
                  }
                />
              ))}
            </RadioButtonGroup>

            {addressOptions.length > 0 && (
              <Select
                id={`survivor-primary-address-${name.key}`}
                labelText="Primary address"
                size="sm"
                className={styles.address}
                value={
                  name.primaryAddressId !== null
                    ? String(name.primaryAddressId)
                    : ''
                }
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  dispatch({
                    type: 'setPrimaryAddress',
                    key: name.key,
                    addressId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <SelectItem value="" text="No primary address" />
                {addressOptions.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)} text={a.label} />
                ))}
              </Select>
            )}

            {foldedMembers.length > 0 && (
              <ReconcileSourceDocuments
                entities={foldedMembers}
                highlightedEntityId={highlightedEntityId}
                onHighlight={onHighlightEntity}
                onViewDocument={onViewDocument}
                viewedDocumentId={viewedDocumentId}
              />
            )}
          </Tile>
        );
      })}
    </section>
  );
};

export default EntityConsolidationSurvivors;
